import { compareText, moduleByKey, routes } from '@pmai/contracts';
import { Alert, Button, CheckboxGroup, Chip, Spinner, TextField } from '@pmai/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TechLogo } from '../../entities/technologies/tech-logo';
import { queryKeyFor, useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { usePermissions } from '../../shared/auth/session';
import { CardSection } from '../../shared/card-section';
import { useErrorText } from '../../shared/i18n';

/**
 * "Tehnoloģijas / produkti" on the project card: the whole catalogue as chips, the project's own
 * highlighted; a click links/unlinks and saves at once (FR-TECH-007/008); copying to other
 * projects in one operation (FR-TECH-009/010).
 */
export function ProjectTechnologiesSection({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canEdit = usePermissions().includes('projects.edit');
  const catalogue = useApiQuery(routes.technologies.list);
  const linkedOpts = { params: { id: projectId } };
  const linked = useApiQuery(routes.technologies.ofProject, linkedOpts);
  const [failed, setFailed] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState<number | null>(null);
  // One project's chip saves run one after another, so the last click is what stays saved.
  const save = useApiMutation(routes.technologies.setForProject, {
    scope: { id: `technologies-${projectId}` },
  });

  const selected = linked.data?.technologyIds ?? [];
  const toggle = (technologyId: string) => {
    const key = queryKeyFor(routes.technologies.ofProject, linkedOpts);
    const next = selected.includes(technologyId)
      ? selected.filter((id) => id !== technologyId)
      : [...selected, technologyId];
    setFailed(false);
    queryClient.setQueryData(key, { technologyIds: next });
    save.mutate(
      { params: { id: projectId }, body: { technologyIds: next } },
      {
        onError: () => {
          setFailed(true);
          void queryClient.invalidateQueries({ queryKey: key });
        },
      },
    );
  };

  const techs = catalogue.data ?? [];
  const hasCatalogue = techs.length > 0;
  return (
    <CardSection
      id="project.technologies"
      title={t('projects.sections.technologies')}
      actions={
        canEdit &&
        hasCatalogue && (
          <Button
            size="sm"
            variant="secondary"
            icon="copy"
            aria-expanded={copying}
            onClick={() => {
              setCopying((open) => !open);
              setCopied(null);
            }}
          >
            {t('technologies.copyOpen')}
          </Button>
        )
      }
    >
      {catalogue.isPending || linked.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : !hasCatalogue ? (
        <p className="text-sm text-ink-muted">
          <Link to={moduleByKey('technologies').path ?? '/'} className="text-brand-700 hover:underline">
            {t('technologies.noCatalog')}
          </Link>
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          <div role="group" aria-label={t('projects.sections.technologies')} className="flex flex-wrap gap-2">
            {techs.map((tech) => (
              <Chip
                key={tech.id}
                selected={selected.includes(tech.id)}
                onToggle={canEdit ? () => toggle(tech.id) : undefined}
                image={<TechLogo logo={tech.logo} small />}
              >
                {tech.name}
              </Chip>
            ))}
          </div>
          {failed && <Alert tone="danger">{t('technologies.saveFailed')}</Alert>}
          {copied !== null && <Alert tone="success">{t('technologies.copied', { count: copied })}</Alert>}
          {copying && (
            <CopyPanel
              projectId={projectId}
              onCancel={() => setCopying(false)}
              onCopied={(count) => {
                setCopying(false);
                setCopied(count);
              }}
            />
          )}
        </div>
      )}
    </CardSection>
  );
}

/** Other projects ("name · customer", A→Z) with search and checkboxes (FR-TECH-009/010). */
function CopyPanel({
  projectId,
  onCancel,
  onCopied,
}: {
  projectId: string;
  onCancel: () => void;
  onCopied: (count: number) => void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const projects = useApiQuery(routes.projects.options);
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [noneChosen, setNoneChosen] = useState(false);
  const copy = useApiMutation(routes.technologies.copyToProjects, {
    invalidates: [routes.technologies.ofProject],
    onSuccess: (result) => onCopied(result.copied),
  });

  const others = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (projects.data ?? [])
      .filter((p) => p.id !== projectId)
      .filter((p) => !needle || `${p.name}\n${p.customerName}`.toLowerCase().includes(needle))
      .sort((a, b) => compareText(a.name, b.name));
  }, [projects.data, projectId, q]);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-canvas/40 p-4">
      <TextField
        label={t('technologies.copySearch')}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {projects.isPending ? (
        <Spinner label={t('common.loading')} />
      ) : others.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('technologies.noOtherProjects')}</p>
      ) : (
        <CheckboxGroup
          label={t('technologies.copyTitle')}
          columns
          options={others.map((p) => ({ value: p.id, label: `${p.name} · ${p.customerName}` }))}
          value={chosen}
          onChange={(next) => {
            setChosen(next);
            setNoneChosen(false);
          }}
        />
      )}
      {noneChosen && <Alert tone="danger">{t('technologies.copyNone')}</Alert>}
      {copy.error && <Alert tone="danger">{errorText(copy.error)}</Alert>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          size="sm"
          icon="copy"
          loading={copy.isPending}
          onClick={() =>
            chosen.length === 0
              ? setNoneChosen(true)
              : copy.mutate({ params: { id: projectId }, body: { projectIds: chosen } })
          }
        >
          {t('common.copy')}
        </Button>
      </div>
    </div>
  );
}
